/** @jsx jsx */
import {css, jsx } from "@emotion/react";
import {Legend} from "components/legendV2"
import { useEffect, useReducer } from 'react';
import {Fragment, useCallback } from "react";
import { useContext } from "react";
import { WidgetControlsContext } from "./context";
import { ControlsContainer } from "./ControlsContainer";
import { widgetControlsReducer } from "./reducer";
import { WidgetControlsContextState } from "./types";

/**
 * This component allows children to hook into the
 * widget controls context. It also uses the state
 * of that context to display the following widget
 * controls: legend, label toggle, and time adjustment
 */
export function WidgetControls({title = undefined,
                                children,
                                filterOptions = undefined,
																 curatedViewMode}) {
    const initContext:WidgetControlsContextState = {
        hideLabels:false,
		enableLabelsToggle: false,
        filterOptions: filterOptions
    }
    const [contextState, dispatch] = useReducer(
        widgetControlsReducer,
        initContext
    );
    //Since the filter is changed up above the SimpleCard, and these Controls, we need to update the reducer to match
    useEffect(() => {
    	dispatch({type: "setFilterProps", payload: filterOptions});
		}, [filterOptions])

    const onLegendItemClick = useCallback((key)=>{
        dispatch({type:"toggleKey",payload:key})
    },[dispatch]);

    return (
        <WidgetControlsContext.Provider value={{
                                            state:contextState,
                                            dispatch:dispatch
                                        }
        }>
            <div css={widgetControlsCss(curatedViewMode)}>
							{curatedViewMode ?
								<Fragment>
									<div className='WC-Title'>
										{title}
									</div>
									<div className='WC-Controls show-on-card-hover'>
										<ControlsContainer
											hideLabels={contextState.hideLabels}
											toggleLabels={() => dispatch({ type: 'toggleLabels' })}
											filterOptions={contextState.filterOptions}
											enableLabelsToggle={contextState.enableLabelsToggle}
                                            legendItemProps={
                                                contextState?.legend?.legendItemProps
                                            }/>
									</div>
								</Fragment> : null
							}
                <div className="WC-Legend">
                    {contextState.legend 
                        ? <Legend   {...contextState.legend}
                                    data={contextState.legend.keys}
                                    sm
                                    onClick={onLegendItemClick}
                                    dispatch={dispatch}/>
                        : null}
                    
                </div>
                <div className="WC-Body">
                    {children}
                </div>
            </div>
        </WidgetControlsContext.Provider>
    )
}

const widgetControlsCss = curatedViewMode => css`
    display: grid;
    grid-template-areas: ${curatedViewMode ? 
    '"title controls controls" "body    body   legend"' : '"body    body   legend"'
    };
    grid-template-columns: 1fr auto auto;
    justify-content: center;
    align-items: stretch;
    width:100%;
    height:100%;

    .WC-Title {
        grid-area: title;
    }
    .WC-Controls {
        grid-area: controls;
        justify-self: end;
    }
    .WC-Legend {
        grid-area: legend;
    }
    .WC-Body {
        grid-area: body;
        display: flex;
        width: 100%;
    }	
`

/**
 * This context uses the reducer pattern to provide the state of the
 * context as well as a dispatch function which can be used to update
 * that state.
 * 
 * Note: If this is used outside of a WidgetControls context dispatch will
 * have no effect and state will be undefined.
 */
export const useWidgetControlsContext = () => {
    const {dispatch, state} = useContext(WidgetControlsContext)
    if (dispatch === undefined) return {dispatch:(_)=>undefined,state}
    return {dispatch, state}
}


